import { Clip } from '@goldcast/player-config/src/types/clip-types';
import { loadClip } from '@goldcast/player-config/src/retrievers/loadClip';
import { updateClip } from '@goldcast/player-config/src/retrievers/updateClip';
import { showErrorToast } from '@/libs/toast/toast';
import { retryAsync } from '@/libs/utils';

/**
 * Syncs the current clip's deletes across all translated languages.
 * Only applies if selected_lang === original_lang (to prevent circular updates).
 *
 * When a delete operation is performed on the original language, the same deletes
 * are applied to all other language variants via the partial update endpoint.
 */
export async function syncDeletesAcrossLanguages(clip: Clip): Promise<void> {
  const { asset_metadata } = clip;

  // Only sync if we're editing the original language
  if (asset_metadata.selected_lang !== asset_metadata.original_lang) {
    return;
  }

  // Only sync if there are translated languages
  if (!asset_metadata.translated_langs || asset_metadata.translated_langs.length < 2) {
    return;
  }
  // Apply the same deletes to all translated languages
  const updatePromises = asset_metadata.translated_langs
    .filter(lang => lang.code !== asset_metadata.original_lang)
    .map(async lang => {
      try {
        const clipInLang: Clip = await retryAsync(() => loadClip(clip.id, `&selected_lang=${lang.code}`), 3);
        const clipToUpdate = {
          ...clipInLang,
          asset_metadata: {
            ...clipInLang.asset_metadata,
            deletes: clip.asset_metadata.deletes,
            selected_lang: lang.code
          }
        };
        await retryAsync(() => updateClip(clipToUpdate), 3);
      } catch (error) {
        window.logger?.error(`Failed to sync deletes for language ${lang.code}:`, error);
        showErrorToast(`Failed to sync deletes to ${lang.name}`);
      }
    });

  await Promise.all(updatePromises);
}
